import { useState, useEffect, useRef } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Select from '@cloudscape-design/components/select'
import Toggle from '@cloudscape-design/components/toggle'
import Button from '@cloudscape-design/components/button'
import Box from '@cloudscape-design/components/box'
import Alert from '@cloudscape-design/components/alert'
import Tabs from '@cloudscape-design/components/tabs'
import SegmentedControl from '@cloudscape-design/components/segmented-control'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Table from '@cloudscape-design/components/table'
import { ArchitectureDiagram } from '@/components/benchmark/ArchitectureDiagram'
import { useAppStore } from '@/store/app-store'
import { credentialsManager } from '@/engine/credentials-manager'
import { isCognitoConfigured } from '@/engine/cognito-auth'
import {
  isFileSystemAccessSupported,
  selectFolder,
  hasFolderAccess,
  releaseFolder,
  registerCleanupOnUnload,
} from '@/engine/fs-storage'
import type { AuthMethod } from '@/types'
import { API_BASE } from '@/config/api'

const REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
]

interface LatencySim {
  enabled: boolean
  connectDelayMs: number
  ttftDelayMs: number
  chunkDelayMs: number
}

export function SettingsPanel() {
  const {
    executionMode, setExecutionMode,
    apiGatewayUrl, setApiGatewayUrl,
    selectedRegion, setSelectedRegion,
    credentialStatus, credentialError, setCredentialStatus,
    hasAnthropicKey, hasXaiKey, setApiKeyState,
    isRunning,
    localFolderEnabled, setLocalFolderEnabled,
    awsAccountId,
    isAuthenticated,
  } = useAppStore()

  const [sim, setSim] = useState<LatencySim>({ enabled: false, connectDelayMs: 0, ttftDelayMs: 0, chunkDelayMs: 0 })
  const [authMethod, setAuthMethod] = useState<AuthMethod>(credentialsManager.getAuthMethod())
  const [accessKeyId, setAccessKeyId] = useState('')
  const [secretAccessKey, setSecretAccessKey] = useState('')
  const [sessionToken, setSessionToken] = useState('')
  const [ssoStartUrl, setSsoStartUrl] = useState(() => localStorage.getItem('bedrock-diag-sso-start-url') || '')
  const [ssoRegion, setSsoRegion] = useState(() => localStorage.getItem('bedrock-diag-sso-region') || 'us-east-1')
  const [showSecret, setShowSecret] = useState(false)
  const [showSessionToken, setShowSessionToken] = useState(false)
  const [showAnthropicKey, setShowAnthropicKey] = useState(false)
  const [showXaiKey, setShowXaiKey] = useState(false)
  const [anthropicKey, setAnthropicKey] = useState('')
  const [xaiKey, setXaiKey] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [ssoDeviceAuth, setSsoDeviceAuth] = useState<{ userCode: string; verificationUriComplete: string } | null>(null)
  const [ssoPolling, setSsoPolling] = useState(false)
  const [ssoAccounts, setSsoAccounts] = useState<{ accountId: string; accountName: string }[]>([])
  const [ssoRoles, setSsoRoles] = useState<{ roleName: string; accountId: string }[]>([])
  const [selectedAccount, setSelectedAccount] = useState('')
  const [selectedRole, setSelectedRole] = useState('')
  const [ssoStep, setSsoStep] = useState<'idle' | 'device' | 'account' | 'role'>('idle')
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [folderSelectError, setFolderSelectError] = useState<string | null>(null)
  const ssoCancelledRef = useRef(false)

  const fsSupported = isFileSystemAccessSupported()

  // Register cleanup handler when folder storage is enabled
  useEffect(() => {
    if (localFolderEnabled) {
      registerCleanupOnUnload()
    }
  }, [localFolderEnabled])

  const handleToggleLocalFolder = async (enabled: boolean) => {
    setFolderSelectError(null)
    if (enabled) {
      try {
        const selected = await selectFolder()
        if (selected) {
          setLocalFolderEnabled(true)
          registerCleanupOnUnload()
        }
        // If user cancelled, don't enable
      } catch (err: any) {
        setFolderSelectError(err.message ?? 'Failed to select folder')
      }
    } else {
      releaseFolder()
      setLocalFolderEnabled(false)
    }
  }

  // Load latency sim from localStorage only (cloud mode fetches on-demand during benchmark)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('bedrock-diag-latency-sim')
      if (stored) setSim(JSON.parse(stored))
    } catch { /* noop */ }
  }, [])

  // Sync API key presence on mount
  useEffect(() => {
    async function syncApiKeys() {
      const hasAnthro = !!(await credentialsManager.getAnthropicApiKey())
      const hasXai = !!(await credentialsManager.getXaiApiKey())
      setApiKeyState(hasAnthro, hasXai)
      if (hasAnthro) setAnthropicKey('••••••••')
      if (hasXai) setXaiKey('••••••••')
    }
    syncApiKeys()
  }, [setApiKeyState])

  // Sync IAM credentials on mount
  useEffect(() => {
    async function syncIamCreds() {
      const creds = await credentialsManager.getIamCredentials()
      if (creds) {
        setAccessKeyId(creds.accessKeyId)
        setSecretAccessKey('••••••••')
        if (creds.sessionToken) setSessionToken('••••••••')
      }
    }
    syncIamCreds()
  }, [])

  const updateSim = async (changes: Partial<LatencySim>) => {
    const updated = { ...sim, ...changes }
    setSim(updated)
    try { localStorage.setItem('bedrock-diag-latency-sim', JSON.stringify(updated)) } catch { /* noop */ }
  }

  const handleUrlChange = (url: string) => {
    setApiGatewayUrl(url)
    if (url && !url.startsWith('https://')) {
      setUrlError('URL must start with https://')
    } else {
      setUrlError(null)
    }
  }

  const handleAuthMethodChange = (method: AuthMethod) => {
    setAuthMethod(method)
    credentialsManager.setAuthMethod(method)
    // Reset credential-related UI and store state
    setCredentialStatus('none')
    useAppStore.getState().setAwsAccountId(null)
    setAccessKeyId('')
    setSecretAccessKey('')
    setSessionToken('')
    setSsoDeviceAuth(null)
    setSsoPolling(false)
    setSsoAccounts([])
    setSsoRoles([])
    setSelectedAccount('')
    setSelectedRole('')
    setSsoStep('idle')
  }

  const handleSaveIamCredentials = async () => {
    const existingCreds = await credentialsManager.getIamCredentials()
    await credentialsManager.setIamCredentials({
      accessKeyId,
      secretAccessKey: secretAccessKey === '••••••••' ? (existingCreds?.secretAccessKey ?? '') : secretAccessKey,
      sessionToken: sessionToken === '••••••••' ? (existingCreds?.sessionToken) : (sessionToken || undefined),
    })
    setCredentialStatus('entered')
  }

  const handleVerifyCredentials = async () => {
    await handleSaveIamCredentials()
    setIsVerifying(true)
    setCredentialStatus('verifying')
    try {
      const result = await credentialsManager.verifyCredentials()
      if (result.success) {
        setCredentialStatus('verified')
        useAppStore.getState().setAwsAccountId(result.account ?? null)
      } else {
        setCredentialStatus('error', result.error ?? 'Verification failed')
        useAppStore.getState().setAwsAccountId(null)
      }
    } catch (err: any) {
      setCredentialStatus('error', err.message ?? 'Verification failed')
      useAppStore.getState().setAwsAccountId(null)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleClearCredentials = async () => {
    ssoCancelledRef.current = true
    await credentialsManager.clearCredentials()
    setAccessKeyId('')
    setSecretAccessKey('')
    setSessionToken('')
    setSsoDeviceAuth(null)
    setSsoPolling(false)
    setIsVerifying(false)
    setSsoAccounts([])
    setSsoRoles([])
    useAppStore.getState().setAwsAccountId(null)
    setSelectedAccount('')
    setSelectedRole('')
    setSsoStep('idle')
    setCredentialStatus('none')
  }

  const handleSsoLogin = async () => {
    if (!ssoStartUrl.trim()) {
      setCredentialStatus('error', 'SSO Start URL is required')
      return
    }
    // Persist SSO config for next visit
    localStorage.setItem('bedrock-diag-sso-start-url', ssoStartUrl.trim())
    localStorage.setItem('bedrock-diag-sso-region', ssoRegion)
    ssoCancelledRef.current = false
    setIsVerifying(true)
    setCredentialStatus('verifying')
    setSsoDeviceAuth(null)
    setSsoStep('device')
    try {
      const deviceAuth = await credentialsManager.initiateSsoLogin({
        startUrl: ssoStartUrl.trim(),
        region: ssoRegion,
      })
      if (ssoCancelledRef.current) return
      setSsoDeviceAuth({
        userCode: deviceAuth.userCode,
        verificationUriComplete: deviceAuth.verificationUriComplete,
      })
      window.open(deviceAuth.verificationUriComplete, '_blank')
      setSsoPolling(true)
      try {
        await credentialsManager.pollSsoToken(deviceAuth)
        if (ssoCancelledRef.current) return
        setSsoPolling(false)
        setSsoDeviceAuth(null)
        setSsoStep('account')
        setLoadingAccounts(true)
        setCredentialStatus('entered')
        try {
          const accounts = await credentialsManager.listSsoAccounts()
          if (ssoCancelledRef.current) return
          setSsoAccounts(accounts ?? [])
          if (accounts.length === 1) {
            setSelectedAccount(accounts[0].accountId)
            setSsoStep('role')
            setLoadingRoles(true)
            try {
              const roles = await credentialsManager.listSsoRoles(accounts[0].accountId)
              if (ssoCancelledRef.current) return
              setSsoRoles(roles ?? [])
              if (roles.length === 1) {
                await handleSsoAssumeRole(accounts[0].accountId, roles[0].roleName)
              }
            } catch (err: any) {
              if (!ssoCancelledRef.current) setCredentialStatus('error', err.message ?? 'Failed to list roles')
            } finally {
              if (!ssoCancelledRef.current) setLoadingRoles(false)
            }
          }
        } catch (err: any) {
          if (!ssoCancelledRef.current) setCredentialStatus('error', err.message ?? 'Failed to list accounts')
        } finally {
          if (!ssoCancelledRef.current) setLoadingAccounts(false)
        }
      } catch (err: any) {
        if (!ssoCancelledRef.current) { setCredentialStatus('error', err.message ?? 'SSO authorization failed'); setSsoStep('idle') }
      } finally {
        if (!ssoCancelledRef.current) { setSsoPolling(false); setSsoDeviceAuth(null) }
      }
    } catch (err: any) {
      if (!ssoCancelledRef.current) { setCredentialStatus('error', err.message ?? 'SSO login failed'); setSsoStep('idle') }
    } finally {
      if (!ssoCancelledRef.current) setIsVerifying(false)
    }
  }

  const handleSsoAccountSelect = async (accountId: string) => {
    setSelectedAccount(accountId)
    setSelectedRole('')
    setSsoRoles([])
    setSsoStep('role')
    setLoadingRoles(true)
    try {
      const roles = await credentialsManager.listSsoRoles(accountId)
      setSsoRoles(roles ?? [])
      if (roles.length === 1) {
        await handleSsoAssumeRole(accountId, roles[0].roleName)
      }
    } catch (err: any) {
      setCredentialStatus('error', err.message ?? 'Failed to list roles')
    } finally {
      setLoadingRoles(false)
    }
  }

  const handleSsoAssumeRole = async (accountId: string, roleName: string) => {
    setSelectedRole(roleName)
    setCredentialStatus('verifying')
    try {
      await credentialsManager.assumeSsoRole(accountId, roleName)
      const verifyResult = await credentialsManager.verifyCredentials()
      if (verifyResult.success) {
        setCredentialStatus('verified')
        useAppStore.getState().setAwsAccountId(verifyResult.account ?? null)
        setSsoStep('idle')
      } else {
        setCredentialStatus('error', verifyResult.error ?? 'SSO credentials could not be verified')
        useAppStore.getState().setAwsAccountId(null)
      }
    } catch (err: any) {
      setCredentialStatus('error', err.message ?? 'Failed to assume SSO role')
    }
  }

  const handleSetAnthropicKey = async (key: string) => {
    setAnthropicKey(key)
    if (key && key !== '••••••••') {
      await credentialsManager.setAnthropicApiKey(key)
      setApiKeyState(true, hasXaiKey)
    }
  }

  const handleClearAnthropicKey = async () => {
    await credentialsManager.clearAnthropicApiKey()
    setAnthropicKey('')
    setApiKeyState(false, hasXaiKey)
  }

  const handleSetXaiKey = async (key: string) => {
    setXaiKey(key)
    if (key && key !== '••••••••') {
      await credentialsManager.setXaiApiKey(key)
      setApiKeyState(hasAnthropicKey, true)
    }
  }

  const handleClearXaiKey = async () => {
    await credentialsManager.clearXaiApiKey()
    setXaiKey('')
    setApiKeyState(hasAnthropicKey, false)
  }

  const credentialStatusIndicator = () => {
    switch (credentialStatus) {
      case 'verified': return <StatusIndicator type="success">Credentials verified{awsAccountId ? ` - Account ${awsAccountId}` : ''}</StatusIndicator>
      case 'verifying': return <StatusIndicator type="loading">Verifying credentials...</StatusIndicator>
      case 'entered': return <StatusIndicator type="info">Credentials entered - click Verify to validate</StatusIndicator>
      case 'error': return <StatusIndicator type="error">{credentialError || 'Verification failed'}</StatusIndicator>
      case 'expired': return <StatusIndicator type="warning">Credentials expired - please re-authenticate</StatusIndicator>
      default: return null
    }
  }

  return (
    <SpaceBetween size="l">

      {/* ── Execution Mode ─────────────────────────────────── */}
      <Container header={<Header variant="h2" description="Choose how benchmark probes are executed. Cloud mode routes through API Gateway; Local mode runs directly from your browser.">Execution Mode</Header>}>
        <SpaceBetween size="s">
          <SegmentedControl
            selectedId={executionMode}
            onChange={({ detail }) => { if (!isRunning) setExecutionMode(detail.selectedId as 'cloud' | 'local') }}
            options={[
              { id: 'cloud', text: '☁️ Cloud' },
              { id: 'local', text: '💻 Local' },
            ]}
          />
          {isRunning && (
            <StatusIndicator type="warning">Mode cannot be changed while a benchmark is running.</StatusIndicator>
          )}

          <ArchitectureDiagram />

          {/* Cloud mode: sign-in required */}
          {executionMode === 'cloud' && isCognitoConfigured() && !isAuthenticated && (
            <Alert type="warning" header="Sign in required for Cloud mode">
              Cloud mode requires authentication to access the API Gateway.
              <Box padding={{ top: 'xs' }}>
                <Button variant="primary" onClick={() => useAppStore.getState().setPage('signin')}>Sign In</Button>
              </Box>
            </Alert>
          )}
          {executionMode === 'cloud' && isAuthenticated && (
            <StatusIndicator type="success">Signed in - Cloud mode ready</StatusIndicator>
          )}
          {executionMode === 'cloud' && !isCognitoConfigured() && (
            <StatusIndicator type="info">Cloud mode - Cognito not configured, using unauthenticated access</StatusIndicator>
          )}
        </SpaceBetween>
      </Container>

      {/* ── Credentials (Local mode) ───────────── */}
      {executionMode === 'local' && (
        <Container header={<Header variant="h2">AWS Credentials</Header>}>
          <SpaceBetween size="m">
            <Tabs
              activeTabId={authMethod}
              onChange={({ detail }) => handleAuthMethodChange(detail.activeTabId as AuthMethod)}
              tabs={[
                {
                  id: 'iam',
                  label: 'IAM Credentials',
                  content: (
                    <SpaceBetween size="s">
                      <FormField label="Access Key ID">
                        <Input
                          value={accessKeyId}
                          onChange={({ detail }) => setAccessKeyId(detail.value)}
                          placeholder="AKIAIOSFODNN7EXAMPLE"
                        />
                      </FormField>
                      <FormField label="Secret Access Key">
                        <Input
                          value={secretAccessKey}
                          onChange={({ detail }) => setSecretAccessKey(detail.value)}
                          placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                          type={showSecret ? 'text' : 'password'}
                        />
                        <Box variant="small">
                          <Button variant="inline-link" onClick={() => setShowSecret(!showSecret)}>
                            {showSecret ? 'Hide' : 'Reveal'}
                          </Button>
                        </Box>
                      </FormField>
                      <FormField label="Session Token" description="Optional - required for temporary credentials">
                        <Input
                          value={sessionToken}
                          onChange={({ detail }) => setSessionToken(detail.value)}
                          placeholder="FwoGZXIvYXdzEBYaDH..."
                          type={showSessionToken ? 'text' : 'password'}
                        />
                        <Box variant="small">
                          <Button variant="inline-link" onClick={() => setShowSessionToken(!showSessionToken)}>
                            {showSessionToken ? 'Hide' : 'Reveal'}
                          </Button>
                        </Box>
                      </FormField>
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'sso',
                  label: 'AWS SSO',
                  content: (
                    <SpaceBetween size="s">
                      <FormField label="SSO Start URL">
                        <Input
                          value={ssoStartUrl}
                          onChange={({ detail }) => setSsoStartUrl(detail.value)}
                          placeholder="https://my-org.awsapps.com/start"
                          disabled={ssoPolling}
                        />
                      </FormField>
                      <FormField label="SSO Region">
                        <Select
                          selectedOption={{ value: ssoRegion, label: ssoRegion }}
                          onChange={({ detail }) => setSsoRegion(detail.selectedOption.value ?? 'us-east-1')}
                          options={REGIONS.map(r => ({ value: r.value, label: r.value }))}
                          disabled={ssoPolling}
                        />
                      </FormField>
                      <Button
                        variant="primary"
                        onClick={handleSsoLogin}
                        disabled={isVerifying || ssoPolling || !ssoStartUrl.trim()}
                        loading={ssoPolling || isVerifying}
                      >
                        {ssoPolling ? 'Waiting for authorization...' : isVerifying ? 'Connecting...' : 'Login with SSO'}
                      </Button>
                      {ssoDeviceAuth && (
                        <Box>
                          <StatusIndicator type="info">
                            Complete authorization in the browser tab that just opened.
                          </StatusIndicator>
                          <Box variant="p">
                            Your code: <Box variant="code" display="inline">{ssoDeviceAuth.userCode}</Box>
                          </Box>
                          <Box variant="small" color="text-body-secondary">
                            If the tab didn't open, go to: <a href={ssoDeviceAuth.verificationUriComplete} target="_blank" rel="noopener noreferrer">{ssoDeviceAuth.verificationUriComplete}</a>
                          </Box>
                        </Box>
                      )}
                      {ssoStep === 'account' && (
                        <FormField label="Select AWS Account">
                          {loadingAccounts ? (
                            <StatusIndicator type="loading">Loading accounts...</StatusIndicator>
                          ) : ssoAccounts.length === 0 ? (
                            <StatusIndicator type="error">No accounts found for this SSO portal.</StatusIndicator>
                          ) : (
                            <Select
                              selectedOption={selectedAccount ? { value: selectedAccount, label: ssoAccounts.find(a => a.accountId === selectedAccount)?.accountName ?? selectedAccount } : null}
                              onChange={({ detail }) => handleSsoAccountSelect(detail.selectedOption.value ?? '')}
                              options={ssoAccounts.map(a => ({ value: a.accountId, label: `${a.accountName} (${a.accountId})` }))}
                              placeholder="Choose an account"
                            />
                          )}
                        </FormField>
                      )}
                      {ssoStep === 'role' && (
                        <FormField label="Select Role">
                          {loadingRoles ? (
                            <StatusIndicator type="loading">Loading roles...</StatusIndicator>
                          ) : ssoRoles.length === 0 ? (
                            <StatusIndicator type="error">No roles found for this account.</StatusIndicator>
                          ) : (
                            <Select
                              selectedOption={selectedRole ? { value: selectedRole, label: selectedRole } : null}
                              onChange={({ detail }) => handleSsoAssumeRole(selectedAccount, detail.selectedOption.value ?? '')}
                              options={ssoRoles.map(r => ({ value: r.roleName, label: r.roleName }))}
                              placeholder="Choose a role"
                            />
                          )}
                        </FormField>
                      )}
                      <Box variant="small" color="text-body-secondary">
                        SSO login uses the serverless backend to proxy OIDC calls.
                      </Box>
                    </SpaceBetween>
                  ),
                },
              ]}
            />

            {credentialStatus !== 'none' && credentialStatusIndicator()}

            <SpaceBetween direction="horizontal" size="xs">
              {authMethod === 'iam' && (
                <Button
                  variant="primary"
                  onClick={handleVerifyCredentials}
                  disabled={isVerifying || !accessKeyId}
                  loading={isVerifying}
                >
                  Verify
                </Button>
              )}
              <Button variant="normal" onClick={handleClearCredentials}>
                Clear
              </Button>
            </SpaceBetween>

            <Box variant="small" color="text-body-secondary">
              Credentials are encrypted with AES-256-GCM (Web Crypto API) in sessionStorage.
              The encryption key exists only in memory and is lost when this tab is closed.
            </Box>
          </SpaceBetween>
        </Container>
      )}

      {/* ── Recommended IAM Policy (Local mode) ──────────── */}
      {executionMode === 'local' && (
        <Container header={<Header variant="h2">Recommended IAM Policy</Header>}>
          <SpaceBetween size="s">
            <Box variant="small" color="text-body-secondary">
              Create a dedicated IAM user or role with only these permissions for Local Mode:
            </Box>
            <Box variant="code">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 11 }}>{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "bedrock:InvokeModel",
      "bedrock:InvokeModelWithResponseStream",
      "sts:GetCallerIdentity",
      "ec2:DescribeCapacityBlockOfferings",
      "sagemaker:SearchTrainingPlanOfferings"
    ],
    "Resource": "*"
  }]
}`}</pre>
            </Box>
          </SpaceBetween>
        </Container>
      )}

      {/* ── API Keys (Local mode) ──────────────────── */}
      {executionMode === 'local' && (
        <Container header={<Header variant="h2" description="API keys for Anthropic and Grok (xAI) benchmarks. Keys are encrypted in sessionStorage.">API Keys</Header>}>
          <SpaceBetween size="m">
            <FormField label="Anthropic API Key">
              <SpaceBetween direction="horizontal" size="xs">
                <div style={{ flex: 1 }}>
                  <Input
                    value={anthropicKey}
                    onChange={({ detail }) => handleSetAnthropicKey(detail.value)}
                    placeholder="sk-ant-api03-..."
                    type={showAnthropicKey ? 'text' : 'password'}
                  />
                </div>
                <Button variant="inline-link" onClick={() => setShowAnthropicKey(!showAnthropicKey)}>
                  {showAnthropicKey ? 'Hide' : 'Reveal'}
                </Button>
                <Button variant="normal" onClick={handleClearAnthropicKey} disabled={!hasAnthropicKey}>
                  Clear
                </Button>
              </SpaceBetween>
              {hasAnthropicKey && <StatusIndicator type="success">Key stored</StatusIndicator>}
            </FormField>

            <FormField label="xAI API Key">
              <SpaceBetween direction="horizontal" size="xs">
                <div style={{ flex: 1 }}>
                  <Input
                    value={xaiKey}
                    onChange={({ detail }) => handleSetXaiKey(detail.value)}
                    placeholder="xai-..."
                    type={showXaiKey ? 'text' : 'password'}
                  />
                </div>
                <Button variant="inline-link" onClick={() => setShowXaiKey(!showXaiKey)}>
                  {showXaiKey ? 'Hide' : 'Reveal'}
                </Button>
                <Button variant="normal" onClick={handleClearXaiKey} disabled={!hasXaiKey}>
                  Clear
                </Button>
              </SpaceBetween>
              {hasXaiKey && <StatusIndicator type="success">Key stored</StatusIndicator>}
            </FormField>
          </SpaceBetween>
        </Container>
      )}

      {/* ── Local Folder Storage (Local mode, Chromium only) ──── */}
      {executionMode === 'local' && (
        <Container header={<Header variant="h2" description="Save benchmark results as JSON files to a local folder. Results are also kept in IndexedDB as a fallback.">Local Folder Storage</Header>}>
          <SpaceBetween size="s">
            {fsSupported ? (
              <>
                <Toggle
                  checked={localFolderEnabled}
                  onChange={({ detail }) => handleToggleLocalFolder(detail.checked)}
                >
                  Save results to local folder
                </Toggle>
                {localFolderEnabled && hasFolderAccess() && (
                  <StatusIndicator type="success">Folder selected - results will be auto-saved</StatusIndicator>
                )}
                {localFolderEnabled && !hasFolderAccess() && (
                  <StatusIndicator type="warning">Folder access lost - toggle off and on to re-select</StatusIndicator>
                )}
                {folderSelectError && (
                  <StatusIndicator type="error">{folderSelectError}</StatusIndicator>
                )}
                <Box variant="small" color="text-body-secondary">
                  Credentials and API keys are not saved.
                </Box>
              </>
            ) : (
              <Box variant="small" color="text-body-secondary">
                ⚠️ Local folder storage requires Chrome or Edge. This feature is not available in Firefox or Safari because they do not support the File System Access API.
              </Box>
            )}
          </SpaceBetween>
        </Container>
      )}

      {/* ── Supported Providers ───────────────────────────── */}
      <Table
        header={<Header variant="h2">Supported Providers</Header>}
        columnDefinitions={[
          { id: 'provider', header: 'Provider', cell: (item: any) => item.provider },
          { id: 'apiType', header: 'API Type', cell: (item: any) => item.apiType },
          { id: 'auth', header: 'Auth', cell: (item: any) => item.auth },
          { id: 'metrics', header: 'Server-side Metrics', cell: (item: any) => item.metrics },
        ]}
        items={[
          { provider: 'Amazon Bedrock', apiType: 'InvokeModelStream, ConverseStream', auth: 'AWS credentials', metrics: 'TTFT, invocation latency, guardrail timing' },
          { provider: 'Anthropic', apiType: 'SSE streaming (api.anthropic.com)', auth: 'ANTHROPIC_API_KEY', metrics: 'None (client-side only)' },
          { provider: 'Grok (xAI)', apiType: 'OpenAI-compatible SSE (api.x.ai)', auth: 'XAI_API_KEY', metrics: 'Cached tokens only' },
        ]}
        variant="embedded"
      />
    </SpaceBetween>
  )
}

